import { Button } from '@/components/ui/button';
import * as DatePicker from '@/components/ui/date-picker';
import * as Field from '@/components/ui/field';
import { IconButton } from '@/components/ui/icon-button';
import { toaster } from '@/components/ui/toast';
import { type DatePickerValueChangeDetails, parseDate } from '@ark-ui/solid';
import { CalendarDateTime, getLocalTimeZone, parseDateTime, today } from '@internationalized/date';
import { createForm, setValue, valiForm } from '@modular-forms/solid';
import { userId } from '@state/auth';
import { useCalendarState } from '@state/events/calendar-state';
import { useMutation } from '@tanstack/solid-query';
import dayjs from 'dayjs';
import { Calendar, X } from 'lucide-solid';
import { Show, createMemo, createSignal } from 'solid-js'; // Added Show
import { Index, Portal } from 'solid-js/web';
import { useService } from 'solid-services';
import { queryClient } from 'src/App';
import { flex, grid } from 'styled-system/patterns'; // Added flex
import { input } from 'styled-system/recipes';
import { updateEvent } from '../Edit/api';
import { type OverrideForm, OverrideSchema } from './eventSchema';

export default function OverrideEventForm() {
  const { activeEvent } = useService(useCalendarState)();

  // ADD THIS BASELINE LOG
  console.log('--- FORM OPENED ---');
  console.log('Event Name:', activeEvent()?.group);
  console.log('Original DB starts_at (UTC):', activeEvent()?.starts_at);
  console.log('Current DB override_starts_at (UTC):', activeEvent()?.override_starts_at);

  // 1. Setup Mutation
  const mutation = useMutation(() => ({
    mutationFn: (values: OverrideForm) => {
      const event = activeEvent();
      if (!event?.id) throw new Error('No active event selected to override.');
      return updateEvent({ id: event.id, values });
    },
    // ADD 'data' parameter here to log the DB response!
    onSuccess: (data) => {
      console.log('--- SERVER SAVED & RETURNED ---');
      console.log('DB Row Data:', data);
      console.log('-------------------------------');

      toaster.create({ title: 'Override saved', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['events', userId()] });
    },
    onError: (error) => {
      toaster.create({ title: 'Error', description: error.message, type: 'error' });
    },
  }));

  // 2. Setup Form
  const [overrideForm, { Form, Field: FormField }] = createForm<OverrideForm>({
    validate: valiForm(OverrideSchema),
    initialValues: {
      override_target_date: activeEvent()?.override_target_date || '',
      override_starts_at: activeEvent()?.override_starts_at || '',
    }
  });

  const handleSubmit = (values: OverrideForm) => {
    let utcStartsAt = values.override_starts_at;

    console.log('--- FORM SUBMIT TRIGGERED ---');
    console.log('1. Raw Form Target Date:', values.override_target_date);
    console.log('2. Raw Form Start Time (Local UI string):', values.override_starts_at);

    if (values.override_starts_at) {
      const localDateTime = parseDateTime(values.override_starts_at);
      utcStartsAt = localDateTime.toDate(getLocalTimeZone()).toISOString();
    }

    console.log('3. Converted Start Time (UTC to send to DB):', utcStartsAt);
    console.log('-----------------------------');

    mutation.mutate({
      ...values,
      override_starts_at: utcStartsAt,
    });
  };

  return (
    <Form onSubmit={handleSubmit} class={grid({ gap: '4', w: 'full' })}>

      {/* Target Date (Date Picker Only) */}
      <FormField name="override_target_date">
        {(field) => {
          const value = createMemo(() => {
            if (!field.value) return undefined;
            try {
              return [parseDate(dayjs(field.value).format('YYYY-MM-DD'))];
            } catch {
              return undefined;
            }
          });
          const [open, setOpen] = createSignal(false);

          return (
            <Field.Root invalid={!!field.error}>
              <DatePicker.Root
                value={value()}
                onValueChange={(e) => setValue(overrideForm, 'override_target_date', e.value[0] ? e.value[0].toString() : '')}
                open={open()}
                onOpenChange={() => setOpen(!open())}
              >
                <DatePicker.Label>Target Date</DatePicker.Label>
                <DatePicker.Control>
                  <DatePicker.Input class={input({ size: 'sm' })} placeholder="YYYY-MM-DD" />
                  <DatePicker.Trigger asChild={props => <IconButton size='sm' variant="outline" {...props}><Calendar /></IconButton>} />
                  <DatePicker.ClearTrigger asChild={props => <IconButton size='sm' variant="outline" {...props}><X /></IconButton>} />
                </DatePicker.Control>

                <Portal>
                  <DatePicker.Positioner zIndex={open() ? 'modal!' : 'auto'}>
                    <DatePicker.Content>
                      <DatePicker.YearSelect />
                      <DatePicker.MonthSelect />

                      <DatePicker.View view="day">
                        <DatePicker.Context>
                          {(context) => (
                            <>
                              <DatePicker.ViewControl>
                                <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                                <DatePicker.ViewTrigger><DatePicker.RangeText /></DatePicker.ViewTrigger>
                                <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                              </DatePicker.ViewControl>
                              <DatePicker.Table>
                                <DatePicker.TableHead>
                                  <DatePicker.TableRow>
                                    <Index each={context().weekDays}>
                                      {(weekDay) => <DatePicker.TableHeader>{weekDay().short}</DatePicker.TableHeader>}
                                    </Index>
                                  </DatePicker.TableRow>
                                </DatePicker.TableHead>
                                <DatePicker.TableBody>
                                  <Index each={context().weeks}>
                                    {(week) => (
                                      <DatePicker.TableRow>
                                        <Index each={week()}>
                                          {(day) => (
                                            <DatePicker.TableCell value={day()}>
                                              <DatePicker.TableCellTrigger>{day().day}</DatePicker.TableCellTrigger>
                                            </DatePicker.TableCell>
                                          )}
                                        </Index>
                                      </DatePicker.TableRow>
                                    )}
                                  </Index>
                                </DatePicker.TableBody>
                              </DatePicker.Table>
                            </>
                          )}
                        </DatePicker.Context>
                      </DatePicker.View>
                    </DatePicker.Content>
                  </DatePicker.Positioner>
                </Portal>
              </DatePicker.Root>
              <Field.HelperText>The specific date in the recurrence you want to change.</Field.HelperText>
              <Field.ErrorText>{field.error}</Field.ErrorText>
            </Field.Root>
          );
        }}
      </FormField>

      {/* Override Time (Date + Time Picker) */}
      <FormField name="override_starts_at">
        {(field) => {
          const value = createMemo(() => {
            if (!field.value) return undefined;
            try {
              return [parseDateTime(dayjs(field.value).format('YYYY-MM-DDTHH:mm:ss'))];
            } catch {
              return undefined;
            }
          });

          const [open, setOpen] = createSignal(false);

          const timeValue = createMemo(() => {
            const v = value()?.[0];
            if (!v) return '';
            return `${String(v.hour).padStart(2, '0')}:${String(v.minute).padStart(2, '0')}`;
          });

          const onTimeChange = (e: Event) => {
            const target = e.currentTarget as HTMLInputElement;
            const [hours, minutes] = target.value.split(':').map(Number);
            const current = value()?.[0] ?? today(getLocalTimeZone()).add({ days: 0 });
            const newDateTime = current.set({ hour: hours, minute: minutes });
            setValue(overrideForm, 'override_starts_at', newDateTime.toString());
          };

          const onDateChange = (e: DatePickerValueChangeDetails) => {
            const newDate = e.value[0];
            if (!newDate) return setValue(overrideForm, 'override_starts_at', '');

            const prevTime = value()?.[0] ?? { hour: 0, minute: 0 };
            const newDateTime = new CalendarDateTime(newDate.year, newDate.month, newDate.day, prevTime.hour, prevTime.minute);
            setValue(overrideForm, 'override_starts_at', newDateTime.toString());
          };

          return (
            <Field.Root invalid={!!field.error}>
              <DatePicker.Root
                value={value() as any}
                onValueChange={onDateChange}
                open={open()}
                onOpenChange={() => setOpen(!open())}
                closeOnSelect={false}
              // format={(date) => {
              //   if (!date) return '';
              //   // Fixed: Formatting the actively selected date value, not the stale activeEvent
              //   return dayjs(date.toString()).format('MMM D, YYYY h:mm A');
              // }}
              >
                <DatePicker.Label>New Start Time</DatePicker.Label>
                <DatePicker.Control>
                  <DatePicker.Input class={input({ size: 'sm' })} placeholder="Select Date & Time" />
                  <DatePicker.Trigger asChild={props => <IconButton size='sm' variant="outline" {...props}><Calendar /></IconButton>} />
                  <DatePicker.ClearTrigger asChild={props => <IconButton size='sm' variant="outline" {...props}><X /></IconButton>} />
                </DatePicker.Control>

                <Portal>
                  <DatePicker.Positioner zIndex={open() ? 'modal!' : 'auto'}>
                    <DatePicker.Content>
                      <DatePicker.YearSelect />
                      <DatePicker.MonthSelect />

                      <DatePicker.View view="day">
                        <DatePicker.Context>
                          {(context) => (
                            <>
                              <DatePicker.ViewControl>
                                <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                                <DatePicker.ViewTrigger><DatePicker.RangeText /></DatePicker.ViewTrigger>
                                <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                              </DatePicker.ViewControl>
                              <DatePicker.Table>
                                <DatePicker.TableHead>
                                  <DatePicker.TableRow>
                                    <Index each={context().weekDays}>
                                      {(weekDay) => <DatePicker.TableHeader>{weekDay().short}</DatePicker.TableHeader>}
                                    </Index>
                                  </DatePicker.TableRow>
                                </DatePicker.TableHead>
                                <DatePicker.TableBody>
                                  <Index each={context().weeks}>
                                    {(week) => (
                                      <DatePicker.TableRow>
                                        <Index each={week()}>
                                          {(day) => (
                                            <DatePicker.TableCell value={day()}>
                                              <DatePicker.TableCellTrigger>{day().day}</DatePicker.TableCellTrigger>
                                            </DatePicker.TableCell>
                                          )}
                                        </Index>
                                      </DatePicker.TableRow>
                                    )}
                                  </Index>
                                </DatePicker.TableBody>
                              </DatePicker.Table>

                              <div style={{ "margin-top": "1rem", "padding-top": "0.75rem", "border-top": "1px solid var(--colors-border)", display: "flex", "align-items": "center", "justify-content": "space-between" }}>
                                <span style={{ "font-size": "0.875rem", "font-weight": 500 }}>Time</span>
                                <input
                                  type="time"
                                  value={timeValue()}
                                  onChange={onTimeChange}
                                  class={input({ size: 'sm' })}
                                  style={{ width: "auto" }}
                                />
                              </div>
                            </>
                          )}
                        </DatePicker.Context>
                      </DatePicker.View>

                    </DatePicker.Content>
                  </DatePicker.Positioner>
                </Portal>
              </DatePicker.Root>
              <Field.HelperText>Select the new date and time for this specific occurrence.</Field.HelperText>
              <Field.ErrorText>{field.error}</Field.ErrorText>
            </Field.Root>
          );
        }}
      </FormField>

      {/* Button Group */}
      <div class={flex({ gap: '3' })}>
        <Button
          type="submit"
          loading={mutation.isPending}
          colorPalette="jade"
          variant="solid"
        >
          Save Override
        </Button>

        {/* Clear Override Button */}
        <Show when={activeEvent()?.override_starts_at || activeEvent()?.override_target_date}>
          <Button
            type="button"
            variant="outline"
            colorPalette="red"
            loading={mutation.isPending}
            onClick={() => {
              // Cast as any to bypass the schema's string validation and send straight nulls to DB
              mutation.mutate({
                override_target_date: null,
                override_starts_at: null,
              } as any);
            }}
          >
            Clear Override
          </Button>
        </Show>
      </div>
    </Form>
  );
}
